import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TextField,
  Collapse,
} from '@mui/material'
import {
  Computer,
  PhoneAndroid,
  Tablet,
  DeleteOutline,
  CheckCircle,
  LocationOn,
  Edit as EditIcon,
  ExpandMore,
  ExpandLess,
  History,
} from '@mui/icons-material'
import { authApi } from '@api/endpoints/auth'
import type { SessionDto } from '@api/types/auth'
import { ErrorDialog } from '@components/ErrorDialog'
import { getSaveFriendlyError, type FriendlyError } from '@utils/errorHandling'

export function UserSessionsSection() {
  const [sessions, setSessions] = useState<SessionDto[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRevoking, setIsRevoking] = useState(false)
  const [showRevokeAllDialog, setShowRevokeAllDialog] = useState(false)
  const [editingSession, setEditingSession] = useState<string | null>(null)
  const [newSessionName, setNewSessionName] = useState('')
  const [showExpired, setShowExpired] = useState(false)
  const [error, setError] = useState<FriendlyError | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Load sessions
  useEffect(() => {
    loadSessions()
  }, [])

  const loadSessions = async () => {
    try {
      setIsLoading(true)
      const data = await authApi.getSessions()
      setSessions(data)
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsLoading(false)
    }
  }

  const handleRevokeSession = async (sessionId: string) => {
    try {
      setIsRevoking(true)
      setError(null)
      setSuccessMessage(null)

      await authApi.revokeSession(sessionId)
      setSuccessMessage('Session revoked successfully.')
      await loadSessions()
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsRevoking(false)
    }
  }

  const handleUpdateSessionName = async (sessionId: string) => {
    try {
      setError(null)
      setSuccessMessage(null)

      await authApi.updateSessionName(sessionId, { sessionName: newSessionName })
      setSuccessMessage('Session name updated successfully.')
      setEditingSession(null)
      setNewSessionName('')
      await loadSessions()
    } catch (err) {
      setError(getSaveFriendlyError(err))
    }
  }

  const startEditingSession = (session: SessionDto) => {
    setEditingSession(session.id)
    setNewSessionName(session.sessionName || '')
  }

  const getDeviceIcon = (deviceInfo: string) => {
    const info = (deviceInfo || '').toLowerCase()
    if (info.includes('mobile') || info.includes('iphone') || info.includes('android')) {
      return <PhoneAndroid />
    } else if (info.includes('tablet') || info.includes('ipad')) {
      return <Tablet />
    }
    return <Computer />
  }

  const isExpired = (session: SessionDto) => {
    return new Date(session.expiresAt).getTime() < Date.now()
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
    return date.toLocaleDateString()
  }

  const getTimeUntilExpiration = (expiresAt: string) => {
    const expiryDate = new Date(expiresAt)
    const now = new Date()
    const diffMs = expiryDate.getTime() - now.getTime()

    if (diffMs <= 0) return 'Expired'

    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''}`
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''}`
    if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''}`
    return 'Less than a minute'
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  const currentSession = sessions.find((s) => s.isCurrentSession)
  const otherSessions = sessions.filter((s) => !s.isCurrentSession)
  const activeSessions = otherSessions.filter((s) => !isExpired(s))
  const expiredSessions = otherSessions.filter((s) => isExpired(s))

  return (
    <Box>
      {/* Error Dialog */}
      {error && (
        <ErrorDialog
          open={true}
          title={error.title}
          message={error.message}
          details={error.details}
          onClose={() => setError(null)}
        />
      )}

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6">Active Sessions</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage devices where you're currently signed in
          </Typography>
        </Box>
      </Box>

      {/* Current Session */}
      {currentSession && (
        <Paper elevation={0} sx={{ p: 0, mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ px: 2, py: 1, backgroundColor: 'primary.light', opacity: 0.1 }}>
            <Typography variant="subtitle2" color="primary">
              Current Session
            </Typography>
          </Box>
          <List disablePadding>
            <ListItem sx={{ py: 1.5, px: 2 }}>
              <Box sx={{ mr: 1.5, color: 'primary.main' }}>
                {getDeviceIcon(currentSession.deviceInfo)}
              </Box>
              <ListItemText
                sx={{ my: 0, pr: 6 }}
                primary={
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={0.5}>
                    <Box display="flex" alignItems="center" gap={1} flex={1} minWidth={0}>
                      <Typography variant="body2" fontWeight={500} noWrap>
                        {currentSession.sessionName || currentSession.deviceInfo}
                      </Typography>
                      <Chip
                        icon={<CheckCircle sx={{ fontSize: 14 }} />}
                        label="Active Now"
                        color="success"
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {currentSession.browserInfo}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Box component="span" display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Box component="span" display="flex" alignItems="center" gap={0.5}>
                      <LocationOn sx={{ fontSize: 12 }} />
                      <Typography component="span" variant="caption" color="text.secondary">
                        {currentSession.ipAddress}
                      </Typography>
                    </Box>
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      Last active: {formatDate(currentSession.lastAccessedAt)}
                    </Typography>
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => startEditingSession(currentSession)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </Paper>
      )}

      {/* Active Sessions */}
      {activeSessions.length > 0 ? (
        <Paper elevation={0} sx={{ p: 0, mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ px: 2, py: 1, backgroundColor: 'background.default' }}>
            <Typography variant="subtitle2">Other Active Sessions</Typography>
          </Box>
          <List disablePadding>
            {activeSessions.map((session, index) => (
              <Box key={session.id}>
                <ListItem sx={{ py: 1.5, px: 2 }}>
                  <Box sx={{ mr: 1.5, color: 'text.secondary' }}>
                    {getDeviceIcon(session.deviceInfo)}
                  </Box>
                  <ListItemText
                    sx={{ my: 0, pr: 10 }}
                    primary={
                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={0.5}>
                        <Box display="flex" alignItems="center" gap={1} flex={1} minWidth={0}>
                          <Typography variant="body2" fontWeight={500}>
                            {session.sessionName || session.deviceInfo}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {session.browserInfo}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box component="span" display="flex" flexDirection="column" gap={0.25}>
                        <Box component="span" display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                          <Box component="span" display="flex" alignItems="center" gap={0.5} minWidth={0}>
                            <LocationOn sx={{ fontSize: 12 }} />
                            <Typography component="span" variant="caption" color="text.secondary">
                              {session.ipAddress}
                            </Typography>
                          </Box>
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            Last active: {formatDate(session.lastAccessedAt)}
                          </Typography>
                        </Box>
                        <Typography component="span" variant="caption" color="warning.main" sx={{ fontWeight: 500 }}>
                          Expires in: {getTimeUntilExpiration(session.expiresAt)}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => startEditingSession(session)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleRevokeSession(session.id)}
                      disabled={isRevoking}
                      color="error"
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < activeSessions.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        </Paper>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          You don't have any other active sessions. This is your only signed-in device.
        </Alert>
      )}

      {/* Expired Sessions Toggle */}
      {expiredSessions.length > 0 && (
        <>
          <Button
            variant="text"
            size="small"
            startIcon={<History />}
            endIcon={showExpired ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setShowExpired(!showExpired)}
            sx={{ mb: 1, textTransform: 'none' }}
          >
            {showExpired ? 'Hide' : 'Show'} expired sessions ({expiredSessions.length})
          </Button>
          <Collapse in={showExpired}>
            <Paper elevation={0} sx={{ p: 0, border: '1px solid', borderColor: 'divider', opacity: 0.7 }}>
              <Box sx={{ px: 2, py: 1, backgroundColor: 'background.default' }}>
                <Typography variant="subtitle2" color="text.secondary">Expired Sessions (Last 30 Days)</Typography>
              </Box>
              <List disablePadding>
                {expiredSessions.map((session, index) => (
                  <Box key={session.id}>
                    <ListItem sx={{ py: 1.5, px: 2 }}>
                      <Box sx={{ mr: 1.5, color: 'text.disabled' }}>
                        {getDeviceIcon(session.deviceInfo)}
                      </Box>
                      <ListItemText
                        sx={{ my: 0 }}
                        primary={
                          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={0.5}>
                            <Box display="flex" alignItems="center" gap={1} flex={1} minWidth={0}>
                              <Typography variant="body2" fontWeight={500} color="text.secondary">
                                {session.sessionName || session.deviceInfo}
                              </Typography>
                              <Chip
                                label="Expired"
                                size="small"
                                sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }}
                              />
                            </Box>
                            <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>
                              {session.browserInfo}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box component="span" display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                            <Box component="span" display="flex" alignItems="center" gap={0.5}>
                              <LocationOn sx={{ fontSize: 12 }} />
                              <Typography component="span" variant="caption" color="text.disabled">
                                {session.ipAddress}
                              </Typography>
                            </Box>
                            <Typography component="span" variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>
                              Last active: {formatDate(session.lastAccessedAt)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < expiredSessions.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            </Paper>
          </Collapse>
        </>
      )}

      {/* Edit Session Name Dialog */}
      <Dialog open={!!editingSession} onClose={() => setEditingSession(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Session Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Session Name"
            type="text"
            fullWidth
            value={newSessionName}
            onChange={(e) => setNewSessionName(e.target.value)}
            placeholder="e.g., My iPhone, Work Laptop"
            helperText="Give this session a memorable name to identify it easily"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingSession(null)}>Cancel</Button>
          <Button
            onClick={() => editingSession && handleUpdateSessionName(editingSession)}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
